import React from 'react'
// libs
import styled, { css } from 'styled-components'
// icons
import SortIconOrigin from '@mui/icons-material/ArrowUpward'
import IconButtonOrigin from '@mui/material/IconButton'
// types
import { IField, OrderDirection } from 'types/graphql'
// context
import { RiskViewAPIContext } from 'containers/Risks/RiskView/layers/API'
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// hooks
import { CustomColumnField } from 'hooks/risks/useCustomColumnFields'
// utils
import { get } from 'lodash'

const SortIcon = styled(SortIconOrigin)<{
  isActive: boolean
  direction: OrderDirection
}>`
  && {
    transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

    ${({ isActive, direction }) =>
      isActive &&
      css`
        transform: rotate(${direction === OrderDirection.Desc ? 180 : 0}deg);
      `}
  }
`

const IconButton = styled(IconButtonOrigin)<{ isActive: boolean }>`
  && {
    ${({ isActive }) =>
      isActive &&
      css`
        background-color: rgba(0, 0, 0, 0.08);
      `}
  }
`

interface Props {
  field: IField & Partial<Pick<CustomColumnField, 'fromCustomColumn' | 'customColumnSources'>>
}

const SortButton: React.FC<Props> = ({ field }) => {
  const { riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const { order, setOrder } = React.useContext(RiskViewAPIContext)

  const isCustomColumn = !!field.fromCustomColumn
  const isActive = field.id === get(order, 'field')
  const isRelatedField = !isCustomColumn && field.entityId !== riskTemplate.id

  const [direction, setDirection] = React.useState<OrderDirection>(
    (isActive && get(order, 'direction')) || OrderDirection.Asc
  )

  // A custom column is ordered by its own id — the sources tell the backend which fields on the
  // forms above the row it has to read, in the order the column concatenates them.
  const baseOrder = {
    field: field.id,
    fieldType: field.fieldType,
    ...(isCustomColumn && { customColumnSources: field.customColumnSources })
  }

  const onSortIconClick = () => {
    if (isActive) {
      setDirection((prev) => {
        let nextDirection

        if (prev === OrderDirection.Asc) {
          nextDirection = OrderDirection.Desc
        } else {
          nextDirection = OrderDirection.Asc
        }

        setOrder({
          ...baseOrder,
          direction: nextDirection,
          ...(!isCustomColumn && { relatedRiskTemplateId: isRelatedField ? field.entityId : null })
        })

        return nextDirection
      })
    } else {
      setOrder({ ...baseOrder, direction })
    }
  }

  return (
    <IconButton data-testid='risk-header-sort-button' size='small' isActive={isActive} onClick={onSortIconClick}>
      <SortIcon fontSize='inherit' isActive={isActive} direction={direction} />
    </IconButton>
  )
}

export default SortButton
